import Scenario from "./Scenario";
import Step from "./Step";
import Utils from "../Utilities";
import store, { StoreActions } from "../../state/store";
import { assert } from "../utilities/ErrorHandling";
import ScenarioUtils from "../utilities/ScenarioUtils";
import { ObjectTriggerTypes, ScenarioElementTypes } from "../../interfaces/enums";
import { SequenceParams, SequenceUIInfo, SequenceDataObject, SequenceSaveData, SequenceExportData } from "../../interfaces/ScenarioElements";
import { TimelineEffectorTypes } from "../../globals";
import { APIField, RegisterClassWithAPI } from "../api/Decorators";
import { getUuidv4 } from "../utilities/Misc";
import { scenarioMap } from ".";

const sequenceDataObject : SequenceDataObject = {
	id: "",
	scenarioId: "",
	name: "",
	type: ScenarioElementTypes.Sequence,
	defaultStepId: "",
	trackerStepId: "",
	recentStepId: "",
	outputId: "",
	parentId: "",
};

/**
 * defines a container of Steps, with a default state step that acts as a shared state between all other steps
 *
 * one sequence in the scenario *must* be set as the initial sequence (done in Redux)
 * @export
 * @class Sequence
 */
@RegisterClassWithAPI("Sequence", {scenario: "Scenario"}, "A container of Steps, with a default state step that acts as a shared state between all other steps. Creating a new sequence class will not automatically add it to the scenario. You must call AddSequenceAction to add it to the scenario. One sequence in the scenario *must* be set as the initial sequence.")
export default class Sequence
{
	#id: string;
	scenario: Scenario;

	@APIField({ type: "Step[]" })
		steps: Step[];
	private _defaultStep: Step | null;
	private _trackersStep: Step | null;
	recentStep: Step | null;
	output: Sequence | undefined;
	uiInfo: SequenceUIInfo;
	index?: number;

	get trackersStep(): Step
	{
		return this._trackersStep!;
	}

	set trackersStep(step: Step)
	{
		this._trackersStep = step;
	}

	get defaultStep(): Step
	{
		return this._defaultStep!;
	}

	set defaultStep(step: Step)
	{
		this._defaultStep = step;
	}

	/**
     * Creates a new instance of a Sequence
     * @param {Scenario} scenario the Scenario the sequence will beong to. *defaults to the currently loaded scenario*
     * @param {{
	 * name: String,
	 * autogenerate: Boolean,
	 * copyDefaultState: Boolean,
	 * position: {x: Number, y: Number}
	 * }} [params={}] optional parameters to modify sequence creation:
	 * - name: create the sequence with this specified name in particular. subject to Utils.getUniqueElementName. *default to "Sequence"*
	 * - autogenerate: if true, the Sequence will automatically create for itself all the necessary data to function correctly such as the default state step. *defaults to 'true'*
	 * - copyDefaultState: if true, the sequence will copy the default state of the previous sequence in the scenario. *defaults to true*
	 * - position: define a specific coordinate to draw the visual representation of this sequence in the Sequence Editor window. *defaults to th eposition of the previous sequence, plus an offset of {x: 30, y: 30}*
     * @memberof Sequence
     */
	constructor(scenario?: Scenario, params: SequenceParams = {})
	{
		const options: SequenceParams = {
			autogenerate: true,
			copyDefaultState: true,
			noFirstStep: false,
			...params
		};

		this.#id = getUuidv4();
		this.scenario = scenario ?? Scenario.current()!; // the ! here is just for convenience; if there's no scenario we throw in the next line
		if(!this.scenario) throw new Error("No scenario provided to Sequence constructor and no scenario is currently loaded");

		this.steps = [];
		this._defaultStep = null;
		this._trackersStep = null;
		this.recentStep = null;

		const sequences = this.scenario.sequences;
		const lastSequence = sequences.at(-1);

		const dataObject: SequenceDataObject = {
			...sequenceDataObject,
			id: this.id,
			name: Utils.getUniqueElementName(sequences, options.name || "Sequence 1"),
			parentId: this.scenario.id,
		};
		store.dispatch(StoreActions.scenario.setElementData({ dataId: this.id, data: dataObject }));

		const suggestedPosition = {
			x: options.position ? Math.max(options.position.x, 0) : 0,
			y: options.position ? Math.max(options.position.y, 0) : 0,
		};

		if (!options.position || (suggestedPosition.x === 0 && suggestedPosition.y === 0))
		{			
			if (lastSequence)
			{
				suggestedPosition.x = lastSequence.uiInfo.x + 30;
				suggestedPosition.y = lastSequence.uiInfo.y + 30;
			}
		}

		this.uiInfo = {
			x: params.position?.x ?? suggestedPosition.x,
			y: params.position?.y ?? suggestedPosition.y,
			isExpanded: true,
			outBox: undefined,
			inBox: undefined,
		};

		if (options.autogenerate)
		{
			if (options.copyDefaultState && lastSequence)
			{
				this._defaultStep = lastSequence.defaultStep.clone({ sequence: this });
			}
			else
			{
				this._defaultStep = new Step(this, {
					name: "Default State",
					connectedToDefault: false,
					isDefaultStep: true,
				});
			}

			if (!options.noFirstStep)
			{
				this.steps.push(new Step(this));
				this.recentStep = this.steps[this.steps.length - 1];
			}

			const initialSequence: Sequence = this.scenario.getInitialSequence() || this;
			if (initialSequence.id === this.id)
			{
				//content placement step
				this._trackersStep = new Step(this, {
					name: "Content Placement",
					isTrackersStep: true,
				});

				this.steps.unshift(this._trackersStep);
			}
			else if (initialSequence.trackersStep?.sequence.id === initialSequence.id)
			{
				this._trackersStep = initialSequence.trackersStep;
			}
		}

		scenarioMap.set(this.id, this);
	}

	getType() : string
	{
		return ScenarioElementTypes.Sequence;
	}

	getDataObject() : SequenceDataObject
	{
		return store.getState().scenarioModule.elementData[this.id];
	}

	get id() : string
	{
		return this.#id;
	}

	set id(newId : string)
	{
		const currentDataObject = { ...this.getDataObject() };
		store.dispatch(StoreActions.scenario.removeElementData(this.id));

		this.#id = newId;
		store.dispatch(StoreActions.scenario.setElementData({ dataId: this.id, data: { ...currentDataObject, id: newId } }));

		this.steps.forEach((step) => step.parentId = newId);
	}

	@APIField({ type: "string" })
	get name() : string
	{
		return this.getDataObject().name;
	}

	set name(newName : string)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "name", propertyValue: newName }));
	}

	get parentId() : string
	{
		return this.getDataObject().parentId;
	}

	set parentId(newParentId: string)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "parentId", propertyValue: newParentId }));
	}

	/**
 * @returns an array of sequences that lead to this sequence by navigational connection
 * @memberof Sequence
 */
	getInputs() : string[]
	{
		return this.scenario.sequences.filter((sequence) => sequence.output === this).map((sequence) => sequence.id);
	}

	/**
 * @returns all the data necessary to save and load this sequence from a scenario save file
 * @memberof Sequence
 */
	save() : SequenceSaveData
	{
		return {
			id: this.id,
			x: this.uiInfo.x,
			y: this.uiInfo.y,
			stepIds: this.steps.map((step) => step.id),
			inObject: this.getInputs(),
			outObject: this.output ? this.output.id : "",
			name: this.name,
			defaultStep: this.defaultStep.id,
			trackersStep: this.trackersStep.id,
			expanded: this.uiInfo.isExpanded,
		};
	}

	/**
 * @returns all the data necessary for playback in the viewer app
 * @memberof Sequence
 */
	export() : SequenceExportData
	{
		const stepsToExport : string[] = [];
		for (const step of this.steps)
		{
			if (!step.isTrackersStep() || step.id === this.trackersStep.id)
			{stepsToExport.push(step.id);}
		}

		return {
			name: this.name,
			next: this.output ? this.output.id : "",
			stepIds: stepsToExport,
			trackersStep: this.trackersStep.id,
		};
	}

	load(data : SequenceSaveData)
	{
		assert(data);

		this.clear();
		scenarioMap.delete(this.id);

		this.id = data.id;
		this.name = data.name;
		this.uiInfo.x = data.x;
		this.uiInfo.y = data.y;
		this.uiInfo.isExpanded = data.expanded !== undefined ? data.expanded : true;

		scenarioMap.set(this.id, this);
	}

	/**
 * removes all steps from this sequence (excluding the default state step)
 * @memberof Sequence
 */
	clear() : void
	{
		this.steps.length = 0;
	}

	/**
 * used to actually remove this sequence from the scenario data
 *
 * disposes of each step as well
 * @memberof Sequence
 */
	dispose()
	{
		this.steps.forEach((step) => step.dispose());
		this.scenario.removeSequence(this);
		scenarioMap.delete(this.id);
	}

	getParent() : Scenario
	{
		return this.scenario;
	}

	getChildren() : Step[]
	{
		return this.steps;
	}

	getTrackersStep() : Step | undefined
	{
		for (let i = 0; i< this.steps.length; i++)
		{
			const step = this.steps[i];
			if (step.isTrackersStep()) return step;
		}
	}

	findStep(stepId : string ) : Step | undefined
	{
		for (let i = 0; i< this.steps.length; i++)
		{
			const step = this.steps[i];
			if (step.id === stepId) return step;
		}
	}

	removeStep(target : Step) : void
	{
		this.steps = this.steps.filter((step) => step !== target);
	}

	moveStep(fromIndex: number, toIndex: number) : void
	{
		const newArray = this.steps.filter((item, idx) => idx !== fromIndex);
		newArray.splice(toIndex, 0, this.steps[fromIndex]);
		this.steps = newArray;
	}

	clone() : Sequence
	{
		const newSequence = new Sequence(this.scenario, {
			name: this.name,
			autogenerate: false,
		});

		newSequence._defaultStep = this.defaultStep.clone({ sequence: newSequence });
		newSequence._trackersStep = this._trackersStep;
		newSequence.steps = this.steps.filter(step => {return !step.isTrackersStep()}).map((step) => step.clone({ sequence: newSequence }));

		const stepRelationsMap = ScenarioUtils.generateStepRelationsMap(newSequence.steps);

		for( const stepId of stepRelationsMap.values())
		{

			let myUIData = store.getState().uiModule.ui[stepId] ? JSON.stringify(store.getState().uiModule.ui[stepId]) : '';
			const myStepConditions = store.getState().scenarioModule.stepConditions[stepId] ? JSON.parse(JSON.stringify(store.getState().scenarioModule.stepConditions[stepId])) : '';
			const myScene = newSequence.findStep(stepId)?.scene;
			const myTimeline = newSequence.findStep(stepId)?.timeline ? newSequence.findStep(stepId)?.timeline : null;

			//UI
			if(myUIData)
			{
				for(const [oldStepId, newStepId] of stepRelationsMap)
				{
					while(myUIData.indexOf(oldStepId) > -1)
					{
						myUIData = myUIData.replace(oldStepId, newStepId);
					}

				}
				store.dispatch(StoreActions.ui.setFromCopy({ stepId: stepId, content: JSON.parse(myUIData) }));
			}

			//Step Conditions
			if(myStepConditions)
			{
				for(const stepCondition of myStepConditions)
				{
					for(const trigger of stepCondition.triggers)
					{
						if(stepRelationsMap.has(trigger.targetStep))
						{
							trigger.targetStep = stepRelationsMap.get(trigger.targetStep);
						}
					}
				}

				store.dispatch(StoreActions.scenario.pasteStepConditions({ stepId: stepId, clipboard: myStepConditions }));
			}

			//Object Triggers
			if(myScene)
			{
				myScene.traverse((object) =>
				{
					if(object?.userData?.triggers?.length > 0)
					{
						for(const trigger of object.userData.triggers)
						{
							if(stepRelationsMap.has(trigger.targetStep))
							{
								trigger.targetStep = stepRelationsMap.get(trigger.targetStep);
							}
						}
					}
				});
			}

			//Timeline Actions
			if(myTimeline)
			{
				myTimeline.actionsTrack?.objects[0].channels[0].fragments.forEach(fragment =>
				{
					if(fragment.effectorType === TimelineEffectorTypes.EventInvocation && fragment.effector.target === ObjectTriggerTypes.MoveToStep)
					{
						if(stepRelationsMap.has(fragment.effector.params))
						{
							fragment.effector.params = stepRelationsMap.get(fragment.effector.params);
						}
					}
				});
			}
		}

		return newSequence;

		/*
        let connections = this.findLinesByInSocket(sequenceId);
        for (let i = 0; i < connections.length; i++)
        {
            connections[i] = this.state.lines[connections[i]].outObject;
        }
        */
	}

	getIndex() : number
	{
		return this.scenario.sequences.indexOf(this);
	}

	/**
 * reverses the order of steps in the sequence, and then calls 'reverse' on each step as well
 * @memberof Sequence
 */
	reverse() : void
	{
		this.steps.reverse();
		this.steps.forEach((step) => step.reverse());
	}

	/**
 * @param {Step} from
 * @returns the step that should follow after the provided step, from this sequence or the next if the given step is the last one in this sequence
 * @memberof Sequence
 */
	getNextStep(from: Step) : Step | undefined
	{
		const fromIndex = this.steps.indexOf(from);
		if (fromIndex > -1)
		{
			if (fromIndex + 1 < this.steps.length)
			{
				return this.steps[fromIndex + 1];
			}
			else if (this.output)
			{
				return this.output.steps[0];
			}
		}

		return;
	}

	/**
 * @param {*} from
 * @returns the step that the given step should back into, from this sequence or the previous one if the given step was the first one in this sequence
 * @memberof Sequence
 */
	getPreviousStep(from: Step) : Step | undefined
	{
		const fromIndex = this.steps.indexOf(from);

		if (fromIndex > -1)
		{
			if (fromIndex - 1 >= 0)
			{
				return this.steps[fromIndex - 1];
			}
			else
			{
				const prevSequence = this.scenario.getPreviousSequence(from.sequence);
				return prevSequence?.steps.at(-1);
			}
		}

		return;
	}

	/**
 * since scenario elements aren't normally disposed of, so that their deletion can be undone, use this function to check if the sequence is supposed to be removed or not
 * @returns true if the sequence was deleted (but not disposed of)
 * @memberof Sequence
 */
	isDeleted() : boolean
	{
		return this.scenario.sequences.indexOf(this) < 0;
	}
}
